import type {
  Boat,
  InventoryEngine,
  InventoryEquipment,
} from '@/modules/inventory/types/boat.types';
import {
  WIZARD_DEFAULT_VALUES,
  type WizardFormData,
} from '@/modules/inventory/components/boat-form/types/wizard.types';
import { mapBoatToFormData } from '@/modules/inventory/components/boat-form/hooks/useWizardDraftResume';
import { BOAT_MONEY_KEYS, stripEditableMoney } from '@/modules/inventory/lib/moneyFields';
import {
  ENGINE_KEYS,
  normalizeValue,
  sameValue,
  type EditableBoat,
  type RelationRow,
  type ScalarValue,
} from '@/modules/inventory/utils/boat-changes';

/**
 * #1177 / #1178 — the canvas form ↔ the shared changes model (utils/boat-changes).
 *
 * The canvas holds a WizardFormData: flat trailer fields, engine and equipment
 * rows as the boat resource returned them, and a location selection. The server
 * boat and the form go through the SAME mapping (boat → form values → editable),
 * so a value the dealer never touched — an AI-applied one included — compares
 * equal and is never sent.
 */

/** The canvas fields the update request writes, by their request names. */
export const CANVAS_SCALAR_KEYS = [
  'condition', 'manufacturer', 'model', 'model_year', 'length_ft', 'beam_in', 'draft_in',
  'seating_capacity', 'exterior_color', 'interior_color', 'hin_number', 'our_price', 'msrp',
  'regular_price', 'sale_price', 'cost_price', 'invoice_amount', 'rigging', 'freight',
  'description', 'short_description', 'status_id',
] as const;

export type CanvasScalarKey = (typeof CANVAS_SCALAR_KEYS)[number];

/**
 * #1408 (D-08) — the scalar keys a viewer may send: without finance.cost.view
 * the money keys are not part of the editable state, so no diff can carry them.
 * A holder gets CANVAS_SCALAR_KEYS unchanged.
 */
export function canvasScalarKeys(canViewCost: boolean): readonly CanvasScalarKey[] {
  if (canViewCost) return CANVAS_SCALAR_KEYS;
  const money: readonly string[] = BOAT_MONEY_KEYS;
  return CANVAS_SCALAR_KEYS.filter((key) => !money.includes(key));
}

/**
 * Held in the form until Create: autosave and Save draft never send them, so a
 * Status picked on the canvas cannot move the boat out of Draft early (the old
 * autosave re-sent status_id = Draft on every write for the same reason).
 */
export const CANVAS_DEFERRED_KEYS = ['status_id'] as const;

/**
 * Shown on the canvas but never sent: the update request has no boat-level slot
 * for them (validated() strips them). Set from the server on the first read only.
 */
export const CANVAS_DISPLAY_KEYS = ['fuel_type', 'engine_type'] as const;

const TRAILER_FORM_KEYS = ['trailer_vin', 'trailer_make', 'trailer_model', 'trailer_axles'] as const;

function engineRow(engine: InventoryEngine): RelationRow {
  const source = engine as unknown as Record<string, unknown>;
  return {
    ...(engine.id ? { id: engine.id } : {}),
    values: Object.fromEntries(ENGINE_KEYS.map((key) => [key, normalizeValue(source[key])])),
  };
}

/**
 * A new row whose name and description are both blank was never filled in: it
 * is not in the listing. A blank row that HAS an id is kept (its values go null
 * and the server answers a 422 on the field): autosave acts on transient states,
 * and an AI row the dealer cleared and paused on must not be deleted with its
 * cost, SKU and source (review I6). A canvas row is removed only by its Remove
 * button.
 */
function isUnfilledNewEquipment(row: InventoryEquipment): boolean {
  return !row.id && (row.item ?? '').trim() === '' && (row.description ?? '').trim() === '';
}

function equipmentRow(row: InventoryEquipment): RelationRow {
  return {
    ...(row.id ? { id: row.id } : {}),
    values: {
      item: normalizeValue((row.item ?? '').trim()),
      description: normalizeValue(typeof row.description === 'string' ? row.description.trim() : row.description),
      price: normalizeValue(row.price),
      cost: normalizeValue(row.cost),
      sku: normalizeValue(row.sku),
    },
  };
}

/**
 * The form's state as the changes model sees it. `trailerId` is the attached
 * trailer: the form has no slot for it. While one is attached its row is kept even
 * when every field is cleared — the canvas clears trailer values, it never removes
 * the trailer asset.
 *
 * #1408 — `canViewCost` false leaves out the money scalars and equipment cost.
 */
export function canvasToEditable(
  values: WizardFormData,
  version: number,
  trailerId: string | null,
  canViewCost = true,
): EditableBoat {
  const source = values as unknown as Record<string, unknown>;
  const scalars: Record<string, ScalarValue> = Object.fromEntries(
    canvasScalarKeys(canViewCost).map((key) => [key, normalizeValue(source[key])]),
  );

  const trailerValues: Record<string, ScalarValue> = Object.fromEntries(
    TRAILER_FORM_KEYS.map((key) => [key, normalizeValue(source[key])]),
  );
  const hasTrailer = trailerId !== null || Object.values(trailerValues).some((value) => value !== null);

  return stripEditableMoney({
    version,
    scalars,
    engines: (values.engines ?? []).map(engineRow),
    trailers: hasTrailer ? [{ ...(trailerId ? { id: trailerId } : {}), values: trailerValues }] : [],
    standard_equipment: (values.standard_equipment ?? []).filter((row) => !isUnfilledNewEquipment(row)).map(equipmentRow),
    optional_equipment: (values.optional_equipment ?? []).filter((row) => !isUnfilledNewEquipment(row)).map(equipmentRow),
    groups: {
      // Replace-all (D30). Sorted so the order the dealer ticked them in is not a change.
      locations: {
        location_ids: [...(values.location_ids ?? [])].sort(),
        primary_location_id: normalizeValue(values.primary_location_id),
      },
      // #1337 — replace-all set, in the dealer's order (order is display order on the site).
      boat_types: { boat_types: [...(values.boat_types ?? [])] },
    },
  }, canViewCost);
}

/** The canvas form values for a server boat (the mapping resume always used). */
export function boatToCanvasValues(boat: Boat): WizardFormData {
  return { ...WIZARD_DEFAULT_VALUES, ...mapBoatToFormData(boat) } as WizardFormData;
}

export function boatToCanvasEditable(boat: Boat, canViewCost = true): EditableBoat {
  return canvasToEditable(boatToCanvasValues(boat), boat.version, boat.trailers?.[0]?.id ?? null, canViewCost);
}

/** A blank canvas — the base a brand-new canvas's first read rebases against. */
export function blankCanvasEditable(): EditableBoat {
  return canvasToEditable(WIZARD_DEFAULT_VALUES, 0, null);
}

/** The form value for one scalar: a cleared text field is '', a cleared number is null. */
export function toFormScalar(key: CanvasScalarKey, value: ScalarValue): unknown {
  if (value !== null) return value;
  const empty = (WIZARD_DEFAULT_VALUES as unknown as Record<string, unknown>)[key];
  return typeof empty === 'string' ? '' : null;
}

/**
 * The form values for an editable state (a rebased, chosen or restored one). Each
 * row keeps its read-only keys (inventory number, source document, dealer_edited_at)
 * from the server boat.
 */
export function editableToCanvasValues(editable: EditableBoat, boat: Boat): Partial<WizardFormData> {
  const scalars = Object.fromEntries(
    CANVAS_SCALAR_KEYS.map((key) => [key, toFormScalar(key, editable.scalars[key] ?? null)]),
  );

  const serverEngines = new Map(
    (boat.engines ?? []).flatMap((engine) => (engine.id ? [[engine.id, engine] as const] : [])),
  );
  const engines = editable.engines.map((row) => ({
    ...(row.id ? (serverEngines.get(row.id) ?? {}) : {}),
    ...(row.id ? { id: row.id } : {}),
    ...row.values,
  })) as unknown as InventoryEngine[];

  const serverEquipment = new Map(
    [...(boat.standard_equipment ?? []), ...(boat.optional_equipment ?? [])].flatMap((row) =>
      row.id ? [[row.id, row] as const] : [],
    ),
  );
  const equipment = (rows: RelationRow[]): InventoryEquipment[] =>
    rows.map((row, index) => ({
      ...(row.id ? (serverEquipment.get(row.id) ?? {}) : {}),
      ...(row.id ? { id: row.id } : {}),
      item: String(row.values.item ?? ''),
      description: (row.values.description as string | null) ?? null,
      sort_order: index,
      price: (row.values.price as number | string | null) ?? null,
      cost: (row.values.cost as number | string | null) ?? null,
      sku: (row.values.sku as string | null) ?? null,
    }));

  const trailer = editable.trailers[0];
  const trailerFields = Object.fromEntries(
    TRAILER_FORM_KEYS.map((key) => {
      const value = trailer?.values[key] ?? null;
      return [key, value === null ? '' : String(value)];
    }),
  );

  const locations = editable.groups.locations as
    | { location_ids?: string[]; primary_location_id?: string | null }
    | undefined;

  return {
    ...(scalars as Partial<WizardFormData>),
    ...(trailerFields as Partial<WizardFormData>),
    engines,
    standard_equipment: equipment(editable.standard_equipment),
    optional_equipment: equipment(editable.optional_equipment),
    boat_types: ((editable.groups.boat_types as { boat_types?: string[] } | undefined)?.boat_types) ?? [],
    location_ids: locations?.location_ids ?? [],
    primary_location_id: locations?.primary_location_id ?? '',
    // #515 — read-only: the attached trailer asset's cost for the invoice auto-calc.
    linked_trailer_cost_price: boatToCanvasValues(boat).linked_trailer_cost_price,
  };
}

/** `current` with the deferred keys reset to the snapshot's: autosave and Save draft never send them. */
export function withDeferredKeysFrom(current: EditableBoat, base: EditableBoat): EditableBoat {
  const scalars = { ...current.scalars };
  for (const key of CANVAS_DEFERRED_KEYS) scalars[key] = base.scalars[key] ?? null;
  return { ...current, scalars };
}

/** Two rows describe the same thing unless a key is set on both with different values. */
function compatible(serverRow: RelationRow, mine: RelationRow): boolean {
  return Object.entries(mine.values).every(([key, value]) => {
    const theirs = serverRow.values[key] ?? null;
    return value === null || theirs === null || sameValue(value, theirs);
  });
}

/**
 * The dealer typed an engine (or the trailer) before a read created one: pair each
 * row the dealer added with the first row the server added that it does not
 * contradict, so the dealer's values become edits to the server's row instead of a
 * second engine. A row that contradicts every server row (another make, another
 * serial) stays the dealer's own new row (review Minor 8). Equipment is not paired
 * — a dealer-added equipment line is its own item.
 */
export function pairAddedRows(merged: EditableBoat, base: EditableBoat): EditableBoat {
  const pair = (rows: RelationRow[], baseRows: RelationRow[]): RelationRow[] => {
    const baseIds = new Set(baseRows.flatMap((row) => (row.id ? [row.id] : [])));
    const local = rows.filter((row) => !row.id);
    if (local.length === 0) return rows;

    const pairedWith = new Map<RelationRow, RelationRow>();
    const unpaired: RelationRow[] = [];
    for (const mine of local) {
      const match = rows.find(
        (row) => row.id && !baseIds.has(row.id) && !pairedWith.has(row) && compatible(row, mine),
      );
      if (match) pairedWith.set(match, mine);
      else unpaired.push(mine);
    }

    const out: RelationRow[] = [];
    for (const row of rows) {
      if (!row.id) continue;
      const mine = pairedWith.get(row);
      if (!mine) {
        out.push(row);
        continue;
      }
      const values = { ...row.values };
      for (const [key, value] of Object.entries(mine.values)) if (value !== null) values[key] = value;
      out.push({ id: row.id, values });
    }
    return [...out, ...unpaired];
  };

  return {
    ...merged,
    engines: pair(merged.engines, base.engines),
    trailers: pair(merged.trailers, base.trailers),
  };
}

const ROW_SECTIONS = ['engines', 'trailers', 'standard_equipment', 'optional_equipment'] as const;

/**
 * After a save: rows the save added (sent without an id) now exist on the server
 * with one. `theirs` is the saved boat as a canvas editable (the server's response).
 *
 * The k-th row `sent` held without an id is the k-th row without an id in `mine`:
 * the form appends rows, so a row the dealer added after the send comes later and
 * stays a new row. Each saved row is found in `theirs` as the first row whose id
 * `sent` did not hold and no other saved row claimed, with every value `sent` set
 * (the server wrote exactly what was sent). That id goes onto the row in both
 * `sent` and `mine`, so the rebase treats it as the server's row and any edit the
 * dealer made while the save was in flight as an edit to that row, not a second
 * row (review I1). A sent row with no server copy (an empty "Add" row the payload
 * skipped) is dropped from `sent`; its `mine` row stays new.
 *
 * Each section is matched on its own, including each equipment list: a row moved
 * between the lists during the save is out of scope.
 */
export function dropSavedNewRows(
  sent: EditableBoat,
  mine: EditableBoat,
  theirs: EditableBoat,
): { sent: EditableBoat; mine: EditableBoat } {
  const nextSent: EditableBoat = { ...sent };
  const nextMine: EditableBoat = { ...mine };

  for (const section of ROW_SECTIONS) {
    const sentIds = new Set(sent[section].flatMap((row) => (row.id ? [row.id] : [])));
    const claimed = new Set<string>();
    const idFor = new Map<number, string>(); // k-th id-less row → the server's id

    let k = 0;
    const outSent: RelationRow[] = [];
    for (const row of sent[section]) {
      if (row.id) {
        outSent.push(row);
        continue;
      }
      const set = Object.entries(row.values).filter(([, value]) => value !== null);
      const copy = set.length === 0
        ? undefined
        : theirs[section].find(
          (candidate) => candidate.id
            && !sentIds.has(candidate.id)
            && !claimed.has(candidate.id)
            && set.every(([key, value]) => sameValue(candidate.values[key] ?? null, value)),
        );
      if (copy?.id) {
        claimed.add(copy.id);
        idFor.set(k, copy.id);
        outSent.push({ id: copy.id, values: row.values });
      }
      k += 1;
    }

    let m = 0;
    nextSent[section] = outSent;
    nextMine[section] = mine[section].map((row) => {
      if (row.id) return row;
      const id = idFor.get(m);
      m += 1;
      return id ? { id, values: row.values } : row;
    });
  }

  return { sent: nextSent, mine: nextMine };
}
